import { IClientAssociationModel } from '../../../../../Types/ClientModelTypes';
import {
  IUIPage,
  UIUrvalConditionObjectType,
} from '../../../../../Types/UrvalTypes';
import { getEmptyPage } from '../../../Utils/urvalUtils';
import {
  append,
  assoc,
  assocPath,
  equals,
  init,
  insert,
  last,
  path,
  Path,
  type,
  clone,
} from 'ramda';
import shortid from 'shortid';
import { createAssocLabel } from './UrvalConditionUtils';
import {
  IUrvalPickerData as IPickUrvalField,
  IUrvalPickerData,
} from './UrvalFieldsUtils';
import { ITreeSection } from '../../../../../BaseComponents/TreeSelect/TreeSelectUtils';

const getAttributeModelPath = (
  associationModelPath: IClientAssociationModel[] | undefined,
  name: string
): string => {
  return [
    ...(associationModelPath ?? []).map(
      associationModel => associationModel.AssociatedCollectionName
    ),
    name,
  ].join('.');
};

const findFieldPickerItemPath = (
  items: ITreeSection<IPickUrvalField>[],
  attributeModelPath: string,
  currentPath: Array<string | number> = []
): Array<string | number> | null => {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (item.data?.type === 'field') {
      if (
        item.data?.modelPath &&
        getAttributeModelPath(
          item.data?.modelPath,
          item.data?.attribute?.AttributeName ?? ''
        ) === attributeModelPath
      ) {
        const newPath = [...currentPath, i];
        return newPath;
      }
    } else if (item.data?.type === 'group') {
      const newPath = [...currentPath, i, 'subList'];
      const result = findFieldPickerItemPath(
        item.subList ?? [],
        attributeModelPath,
        newPath
      );
      if (result) {
        return result;
      }
    } else if (item.data?.type === 'extended' && item.data?.association) {
      if (
        item.data?.modelPath &&
        getAttributeModelPath(
          item.data?.modelPath,
          item.data.association.AssociationName
        ) === attributeModelPath
      ) {
        const newPath = [...currentPath, i];

        return newPath;
      } else {
        const newPath = [...currentPath, i, 'subList'];
        if (item.subList) {
          const result = findFieldPickerItemPath(
            item.subList,
            attributeModelPath,
            newPath
          );
          if (result) {
            return result;
          }
        }
      }
    } else if (
      item.data?.type === 'association' ||
      item.data?.type === 'page'
    ) {
      const newPath = [...currentPath, i, 'subList'];
      if (item.subList) {
        const result = findFieldPickerItemPath(
          item.subList,
          attributeModelPath,
          newPath
        );
        if (result) {
          return result;
        }
      }
    }
  }

  return null;
};

export const createFieldPickerItems = (
  selectableFields: ITreeSection<IUrvalPickerData>[],
  urvalPage: IUIPage
): ITreeSection<IUrvalPickerData>[] => {
  let items: ITreeSection<IUrvalPickerData>[] = selectableFields;

  if (urvalPage) {
    urvalPage.conditionObjects.forEach(conditionObject => {
      if (conditionObject.type !== 'Extended') {
        const itemPath = findFieldPickerItemPath(
          items,
          getAttributeModelPath(
            conditionObject.modelPath,
            conditionObject.attribute.AttributeName
          )
        );

        if (itemPath) {
          const item = path<ITreeSection<IPickUrvalField>>(itemPath, items);

          if (item) {
            if (item.data?.type === 'field') {
              if (item.data?.conditionObject) {
                // Add duplicate field
                const itemIndex = last(itemPath);
                if (typeof itemIndex === 'number') {
                  const dublicateItemIndex = itemIndex + 1;
                  const itemArrayPath = init(itemPath);
                  const itemArray = path<ITreeSection<IPickUrvalField>[]>(
                    itemArrayPath,
                    items
                  );

                  if (itemArray) {
                    const numberOfSameItem = itemArray.filter(
                      element =>
                        element.data?.type === 'field' &&
                        element.value === item.value
                    ).length;
                    const newItemArray = insert(
                      dublicateItemIndex,
                      assoc('duplicateNr', numberOfSameItem, clone(item)),
                      itemArray
                    );
                    items = assocPath(itemArrayPath, newItemArray, items);
                  }
                }
              } else {
                items = assocPath(
                  [...itemPath, 'data', 'conditionObject'],
                  conditionObject,
                  items
                );
                items = assocPath([...itemPath, 'checked'], true, items);
              }
            }
          }
        }
      } else {
        // Extended
        let itemPath = findFieldPickerItemPath(
          items,
          getAttributeModelPath(
            conditionObject.modelPath,
            conditionObject.association.AssociationName
          )
        );

        if (itemPath) {
          const item = path<ITreeSection<IPickUrvalField>>(itemPath, items);

          if (item) {
            if (item.data?.type === 'extended') {
              if (item.data?.conditionObject) {
                // Add Duplicate
                const itemIndex = last(itemPath);
                if (typeof itemIndex === 'number') {
                  const itemArrayPath = init(itemPath);
                  const itemArray = path<ITreeSection<IPickUrvalField>[]>(
                    itemArrayPath,
                    items
                  );

                  if (itemArray) {
                    const numberOfSameItem = itemArray.filter(
                      element =>
                        element.data?.type === 'extended' &&
                        element.value === item.value
                    ).length;
                    const dublicateItemIndex = itemIndex + numberOfSameItem;
                    const newItemArray = insert(
                      dublicateItemIndex,
                      assoc('duplicateNr', numberOfSameItem, clone(item)),
                      itemArray
                    );
                    itemPath = [...init(itemPath), dublicateItemIndex];
                    items = assocPath(itemArrayPath, newItemArray, items);
                  }
                }
              } else {
                items = assocPath(
                  [...itemPath, 'data', 'conditionObject'],
                  conditionObject,
                  items
                );
                items = assocPath([...itemPath, 'checked'], true, items);
              }

              if (conditionObject.condition[0]) {
                if (conditionObject.condition[0].in.length === 1) {
                  const extendedItemPath = [...itemPath, 'subList'];

                  let itemPath2 = findFieldPickerItemPath(
                    selectableFields,
                    getAttributeModelPath(
                      conditionObject.modelPath,
                      conditionObject.association.AssociationName
                    )
                  );

                  if (itemPath2) {
                    const subListSelectableItems2 = path<
                      ITreeSection<IUrvalPickerData>[]
                    >([...itemPath2, 'subList'], selectableFields);

                    if (
                      subListSelectableItems2 &&
                      conditionObject.condition[0] &&
                      conditionObject.condition[0].in[0]
                    ) {
                      const subList = createFieldPickerItems(
                        subListSelectableItems2,
                        conditionObject.condition[0].in[0]
                      );
                      items = assocPath(extendedItemPath, subList, items);
                    }
                  }
                } else if (conditionObject.condition[0].in.length > 1) {
                  // Add pages
                  const extendedItemPath = [...itemPath, 'subList'];

                  let itemPath2 = findFieldPickerItemPath(
                    selectableFields,
                    getAttributeModelPath(
                      conditionObject.modelPath,
                      conditionObject.association.AssociationName
                    )
                  );
                  if (itemPath2) {
                    const subListSelectableItems2 = path<
                      ITreeSection<IPickUrvalField>[]
                    >([...itemPath2, 'subList'], selectableFields);

                    if (subListSelectableItems2) {
                      // Reset
                      items = assocPath(extendedItemPath, [], items);

                      for (
                        let i = 0;
                        i < conditionObject.condition[0].in.length;
                        i++
                      ) {
                        const pagePath = [...extendedItemPath, i];
                        const subList = createFieldPickerItems(
                          subListSelectableItems2,
                          conditionObject.condition[0].in[i]
                        );
                        const pages = path(extendedItemPath, items);

                        if (pages && type(pages) === 'Array') {
                          const newPageItem: ITreeSection<IUrvalPickerData> = {
                            subList: subList,
                            open: false,
                            name: `Sida ${i + 1}`,
                            value: shortid(),
                            id: shortid(),
                            data: {
                              type: 'page',
                            },
                          };
                          items = assocPath(pagePath, newPageItem, items);
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    });
  }

  return items;
};

const getOldUrvalPageIndex = (
  oldUrvalPage: IUIPage,
  item: ITreeSection<IPickUrvalField>
): number => {
  let index = -1;
  if (item.data?.type === 'field' || item.data?.type === 'extended') {
    const allObjects = oldUrvalPage.conditionObjects.filter(
      oldConditionObject =>
        oldConditionObject.id === item.data?.conditionObject?.id
    );
    const currentObject = allObjects[item.duplicateNr ?? 0];
    if (currentObject) {
      index = oldUrvalPage.conditionObjects.findIndex(
        x =>
          x.id === item.data?.conditionObject?.id &&
          allObjects.indexOf(x) === (item.duplicateNr ?? 0)
      );
    }
  }

  return index !== undefined ? index : -1;
};

const getAssociationIndex = (
  newUrvalPage: IUIPage,
  item: ITreeSection<IPickUrvalField>
): number => {
  let index = -1;

  for (let i = 0; i < newUrvalPage.conditionObjects.length; i++) {
    const conditionObject = newUrvalPage.conditionObjects[i];

    if (
      conditionObject.modelPath &&
      conditionObject.modelPath.length > 0 &&
      equals(conditionObject.modelPath, item.data?.modelPath)
    ) {
      index = i;
    } else if (
      (index !== -1 &&
        !equals(
          createAssocLabel(conditionObject.modelPath),
          createAssocLabel(item.data?.modelPath)
        )) ||
      conditionObject.type === 'Extended'
    ) {
      return i;
    }
  }

  return index;
};

const getLastNonAssociatedFieldIndex = (newUrvalPage: IUIPage) => {
  const firstAssociationIndex = newUrvalPage.conditionObjects.findIndex(
    conditionObject =>
      conditionObject.modelPath && conditionObject.modelPath.length > 0
  );

  if (firstAssociationIndex === -1) {
    const firstExtended = newUrvalPage.conditionObjects.findIndex(
      conditionObject => conditionObject.type === 'Extended'
    );

    if (firstExtended === -1) {
      return newUrvalPage.conditionObjects.length;
    } else {
      return firstExtended;
    }
  } else {
    return firstAssociationIndex;
  }
};

const fillNewUrvalPage = (
  items: ITreeSection<IPickUrvalField>[],
  urvalPage: IUIPage
): IUIPage => {
  const newUrvalPage: IUIPage = urvalPage;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.data?.type === 'field') {
      if (item.data?.conditionObject) {
        const oldPageIndex = getOldUrvalPageIndex(urvalPage, item);

        if (oldPageIndex === -1) {
          let newConditionIndex = -1;
          if (item.data?.modelPath && item.data?.modelPath.length > 0) {
            newConditionIndex = getAssociationIndex(newUrvalPage, item);
          } else {
            newConditionIndex = getLastNonAssociatedFieldIndex(newUrvalPage);
          }

          if (newConditionIndex === -1) {
            newConditionIndex = newUrvalPage.conditionObjects.length;
          }

          newUrvalPage.conditionObjects = insert(
            newConditionIndex,
            item.data?.conditionObject,
            newUrvalPage.conditionObjects
          );
        }
      }
    } else if (
      item.data?.type === 'group' ||
      item.data?.type === 'association'
    ) {
      if (item.subList) {
        fillNewUrvalPage(item.subList, urvalPage);
      }
    } else if (item.data?.type === 'extended') {
      const oldPageIndex = getOldUrvalPageIndex(urvalPage, item);

      if (item.data?.conditionObject) {
        item.data?.conditionObject.condition.forEach(condition => {
          condition.in = [];
        });

        const extendedsubList = item.subList;
        if (extendedsubList && extendedsubList.length > 0) {
          const hasMultiplePages = extendedsubList.every(
            subField => subField.data?.type === 'page'
          );

          if (hasMultiplePages) {
            extendedsubList.forEach((extendedSubField, index) => {
              if (
                extendedSubField.data?.type === 'page' &&
                extendedSubField.subList
              ) {
                let newPage = getEmptyPage();

                newPage = fillNewUrvalPage(extendedSubField.subList, newPage);

                if (
                  newPage.conditionObjects.length > 0 &&
                  item.data?.conditionObject
                ) {
                  item.data.conditionObject = assocPath(
                    ['condition', 0, 'in', index],
                    newPage,
                    item.data?.conditionObject
                  );
                }
              }
            });
          } else if (item.data?.conditionObject) {
            let newPage = getEmptyPage();
            newPage = fillNewUrvalPage(extendedsubList, newPage);

            item.data.conditionObject = assocPath(
              ['condition', 0, 'in', 0],
              newPage,
              item.data?.conditionObject
            );
          }
        }

        if (oldPageIndex === -1) {
          newUrvalPage.conditionObjects = append(
            item.data?.conditionObject,
            newUrvalPage.conditionObjects
          );
        } else {
          newUrvalPage.conditionObjects = assocPath(
            [oldPageIndex],
            item.data?.conditionObject,
            newUrvalPage.conditionObjects
          );
        }
      }
    }
  }

  return newUrvalPage;
};

const fillOldUrvalPage = (
  items: ITreeSection<IPickUrvalField>[],
  oldUrvalPage: IUIPage | undefined
): IUIPage => {
  if (!oldUrvalPage) {
    return getEmptyPage();
  }

  const newUrvalPage: IUIPage = { ...oldUrvalPage, conditionObjects: [] };

  oldUrvalPage.conditionObjects.forEach(oldConditionObject => {
    let itemPath: Path | null = null;
    let newConditionObject: UIUrvalConditionObjectType = oldConditionObject;

    if (oldConditionObject.type !== 'Extended') {
      itemPath = findFieldPickerItemPath(
        items,
        getAttributeModelPath(
          oldConditionObject.modelPath,
          oldConditionObject.attribute.AttributeName
        )
      );
    } else {
      itemPath = findFieldPickerItemPath(
        items,
        getAttributeModelPath(
          oldConditionObject.modelPath,
          oldConditionObject.association.AssociationName
        )
      );

      const extendedPages: IUIPage[] | undefined = path(
        ['condition', 0, 'in'],
        oldConditionObject
      );

      if (itemPath) {
        const item = path<ITreeSection<IPickUrvalField>>(itemPath, items);

        if (extendedPages && item && item.data?.type === 'extended') {
          const subList = item.subList;

          if (subList) {
            let newExtendedPages = extendedPages.map(extendedPage =>
              fillOldUrvalPage(subList, extendedPage)
            );

            newExtendedPages = newExtendedPages.filter(
              newExtendedPage => newExtendedPage.conditionObjects.length > 0
            );

            newConditionObject = assocPath(
              ['condition', 0, 'in'],
              newExtendedPages,
              oldConditionObject
            );
          }
        }
      }
    }

    if (itemPath && itemPath.length > 0) {
      const item = path<ITreeSection<IPickUrvalField>>(itemPath, items);

      if (
        item &&
        (item.data?.type === 'field' || item.data?.type === 'extended') &&
        item.data?.conditionObject
      ) {
        newUrvalPage.conditionObjects = append(
          newConditionObject,
          newUrvalPage.conditionObjects
        );
      }
    }
  });

  return newUrvalPage;
};

export const createPageFromFieldPickerItems = (
  fieldPickerItems: ITreeSection<IPickUrvalField>[],
  oldUrvalPage: IUIPage | undefined
): IUIPage => {
  let newUrvalPage = getEmptyPage();
  newUrvalPage = fillOldUrvalPage(fieldPickerItems, oldUrvalPage);
  newUrvalPage = fillNewUrvalPage(fieldPickerItems, newUrvalPage);

  return newUrvalPage;
};
